import { css } from '@emotion/react';
import { useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import t from 'react-translate';
import { AngularContext } from 'react-app';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { createMentorshipProgramGuide, updateMentorshipProgramGuide } from 'redux/actions/mentoring-program-guides';
import { CompletionStatus } from 'redux/schemas/models/mentoring-program-enrollments';
import {
  MentoringProgramEvent,
  MentoringProgramGuide,
  MentoringProgramGuideRequirementType,
  MentoringProgramMilestone,
} from 'redux/schemas/models/mentoring-program-guides';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import NvAthenaFroala, { ResizeType } from 'athena/components/nv-athena-froala';
import { ToolbarOptions } from 'froala/helpers/nv-froala-constants';
import AthenaTextInput from 'athena/components/text-input';
import NvFlyoutModal, { ModalType } from 'shared/components/nv-flyout-modal';
import VideoSection from './video-section';
import RelatedMaterialsSection from './related-materials-section';
import DeliverySection, { AppearsInOptionValues } from './delivery-section';
import { config } from '@config/pendo.config.json';
import { TargetAudience } from 'redux/schemas/models/mentoring-programs';


type CreateGuideFlyoutProps = {
  onClose: (shouldReset: boolean) => void;
  defaultValues?: any;
};

const CreateGuideFlyout = ({
  onClose,
  defaultValues,
}: CreateGuideFlyoutProps) => {
  const translationBase = t.MENTORING_PROGRAMS.GUIDANCE.GUIDES;

  const determineAppearsInForDefaultValues = (defaults) => {
    const { mentorshipProgramGuideRequirement } = defaults;
    const { criteria, type } = mentorshipProgramGuideRequirement;

    switch (type) {
      case MentoringProgramGuideRequirementType.EVENT:
        return AppearsInOptionValues.MY_GOALS;
      case MentoringProgramGuideRequirementType.MILESTONE:
        return AppearsInOptionValues.PROFILE_COMPLETION;
      case MentoringProgramGuideRequirementType.CONNECTION_SESSION:
        return AppearsInOptionValues.SPECIFIC_SESSION;
      case MentoringProgramGuideRequirementType.SESSION:
        if (criteria.sequence?.length > 1) {
          return AppearsInOptionValues.RANGE_OF_SESSIONS;
        }
        return AppearsInOptionValues.SPECIFIC_SESSION;
      default:
        return null;
    }
  };

  const adjustedDefaultValues = {
    title: defaultValues?.title || '',
    content: defaultValues?.content || '',
    video: defaultValues?.video || null,
    relatedMaterials: defaultValues?.attachments || [],
    audience: defaultValues?.role || TargetAudience.MENTOR_AND_MENTEE,
    appearsIn: defaultValues ? determineAppearsInForDefaultValues(defaultValues) : null,
    upperLimit: defaultValues?.mentorshipProgramGuideRequirement?.criteria?.upperLimit || defaultValues?.mentorshipProgramGuideRequirement?.criteria?.sequence?.[0] || null,
    sequenceLowerLimit: defaultValues?.mentorshipProgramGuideRequirement?.criteria?.sequence?.[0] || null,
    sequenceUpperLimit: defaultValues?.mentorshipProgramGuideRequirement?.criteria?.sequence?.[defaultValues.mentorshipProgramGuideRequirement.criteria.sequence.length - 1] || null,
  };

  const isEditMode = !!defaultValues?.id;
  const dispatch = useAppDispatch();
  const { mentoringProgram } = useContext(MentoringProgramContext);

  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current && !isEditMode) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const validationSchema = yup.object().shape({
    title: yup.string().required(),
    content: yup.string().required(),
    video: yup.mixed().nullable(),
    relatedMaterials: yup.array().of(yup.mixed()),
    audience: yup.mixed<TargetAudience>()
      .oneOf(Object.values(TargetAudience)).required(),
    appearsIn: yup.mixed<AppearsInOptionValues>()
      .oneOf(Object.values(AppearsInOptionValues)).required(),
    upperLimit: yup.number().nullable()
      .when('appearsIn', {
        is: AppearsInOptionValues.SPECIFIC_SESSION,
        then: yup.number()
          .integer(t.VALIDATION.POSITIVE_INTEGER())
          .typeError(t.VALIDATION.NUMBER())
          .required(t.VALIDATION.REQUIRED())
          .min(1, t.VALIDATION.MIN('1')),
      }),
    sequenceLowerLimit: yup.number().nullable()
      .when('appearsIn', {
        is: AppearsInOptionValues.RANGE_OF_SESSIONS,
        then: yup.number()
          .integer(t.VALIDATION.POSITIVE_INTEGER())
          .typeError(t.VALIDATION.NUMBER())
          .required(t.VALIDATION.REQUIRED())
          .min(1, t.VALIDATION.MIN('1')),
      }),
    sequenceUpperLimit: yup.number().nullable()
      .when('appearsIn', {
        is: AppearsInOptionValues.RANGE_OF_SESSIONS,
        then: yup.number()
          .integer(t.VALIDATION.POSITIVE_INTEGER())
          .typeError(t.VALIDATION.NUMBER())
          .required(t.VALIDATION.REQUIRED())
          .min(1, t.VALIDATION.MIN('1')),
      }),
  });

  const methods = useForm({
    mode: 'onChange',
    defaultValues: adjustedDefaultValues,
    resolver: yupResolver(validationSchema),
  });

  const {
    formState,
    handleSubmit,
  } = methods;
  const { isDirty } = formState;
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const [isRelatedMaterialsUploading, setIsRelatedMaterialsUploading] = useState(false);

  const determineRequirementTypeForSubmission = (data) => {
    const { appearsIn, upperLimit } = data;
    switch (appearsIn) {
      case AppearsInOptionValues.MY_GOALS:
        return MentoringProgramGuideRequirementType.EVENT;
      case AppearsInOptionValues.PROFILE_COMPLETION:
        return MentoringProgramGuideRequirementType.MILESTONE;
      case AppearsInOptionValues.SPECIFIC_SESSION:
        if (parseInt(upperLimit, 10) === 1) {
          return MentoringProgramGuideRequirementType.CONNECTION_SESSION;
        }
        // falls through: SPECIFIC_SESSION with upperLimit > 1 uses the same requirement type as RANGE_OF_SESSIONS
      case AppearsInOptionValues.RANGE_OF_SESSIONS:
        return MentoringProgramGuideRequirementType.SESSION;
      default:
        return null;
    }
  };

  const determineCriteriaForSubmission = (data) => {
    const { appearsIn, upperLimit, sequenceLowerLimit, sequenceUpperLimit } = data;
    switch (appearsIn) {
      case AppearsInOptionValues.MY_GOALS:
        return {
          trigger: MentoringProgramEvent.MY_GOALS,
        };
      case AppearsInOptionValues.PROFILE_COMPLETION:
        return {
          state: MentoringProgramMilestone.PROFILE_REVIEW,
          status: CompletionStatus.COMPLETED,
        };
      case AppearsInOptionValues.SPECIFIC_SESSION:
        if (parseInt(upperLimit, 10) === 1) {
          return {
            upperLimit: parseInt(upperLimit, 10),
          };
        }
        return {
          sequence: [parseInt(upperLimit, 10)],
        };
      case AppearsInOptionValues.RANGE_OF_SESSIONS:
        return {
          sequence: Array(sequenceUpperLimit - sequenceLowerLimit + 1)
            .fill(0)
            .map((_, idx) => parseInt(sequenceLowerLimit, 10) + idx),
        };
      default:
        return null;
    }
  };

  const handleClose = () => {
    if (isDirty) {
      dispatch(openConfirmationDialog({
        title: translationBase.CREATE_GUIDE_FLYOUT.CONFIRMATION_MODAL.TITLE(),
        bodyText: translationBase.CREATE_GUIDE_FLYOUT.CONFIRMATION_MODAL.BODY(),
        confirmText: translationBase.CREATE_GUIDE_FLYOUT.CONFIRMATION_MODAL.DISCARD(),
        onConfirm: () => onClose(false),
      }));
    } else {
      onClose(false);
    }
  };

  const onSubmit = async (data) => {
    const { relatedMaterials, audience, appearsIn, upperLimit, sequenceLowerLimit, sequenceUpperLimit, ...rest } = data;
    const mentorshipProgramGuide = {
      ...rest,
      attachments: relatedMaterials,
      role: audience,
      requirement: {
        type: determineRequirementTypeForSubmission(data),
        criteria: determineCriteriaForSubmission(data),
      },
    };

    if (isEditMode) {
      await wrapThunkAction(dispatch(updateMentorshipProgramGuide({
        mentoringProgramId: mentoringProgram.id,
        guideId: defaultValues.id,
        mentorshipProgramGuide,
      }))).then(() => {
        onClose(false);
        dispatch(addAlertMessage({
          type: AlertMessageType.SUCCESS,
          header: t.FORM.SUCCESS_BANG(),
          message: translationBase.CREATE_GUIDE_FLYOUT.GUIDE_UPDATED_SUCCESS(),
        }));
      });
    } else {
      await wrapThunkAction(dispatch(createMentorshipProgramGuide({
        mentoringProgramId: mentoringProgram.id,
        mentorshipProgramGuide,
      }))).then(({ payload }) => {
        $state.go(
          $state.current,
          { guideId: (payload as MentoringProgramGuide).id },
          { notify: false }
        );
        onClose(true);
        dispatch(addAlertMessage({
          type: AlertMessageType.SUCCESS,
          header: t.FORM.SUCCESS_BANG(),
          message: translationBase.CREATE_GUIDE_FLYOUT.GUIDE_CREATED_SUCCESS(),
        }));
      });
    }
  };

  return (
    <NvFlyoutModal
      type={ModalType.RIGHT}
      onClose={handleClose}
      width={640}
    >
      <div className='h-100'>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className='h-100 d-flex flex-column'>
            <div className='d-flex justify-content-between position-sticky py-4 px-5 heading-4 border-bottom border-gray-5'>
              {translationBase.CREATE_GUIDE_FLYOUT.GUIDE()}
            </div>
            <div className='py-4 px-5 flex-grow-1 overflow-auto'>
              <div className='semi-bold color-gray-2'>{translationBase.CREATE_GUIDE_FLYOUT.TITLE()}</div>
              <div>
                <AthenaTextInput
                  withForm
                  name='title'
                  required
                  ref={inputRef}
                  showLabel={false}
                  placeholder={translationBase.CREATE_GUIDE_FLYOUT.TITLE_PLACEHOLDER()}
                  maxLength={255}
                  dataQa={config.pendo.athena.mentorshipProgram.guides.addGuideFlyout.guideTitleInputField}
                />
              </div>
              <div className='mt-4 semi-bold color-gray-2'>{translationBase.CREATE_GUIDE_FLYOUT.CONTENT()}</div>
              <div>
                <NvAthenaFroala
                  withForm
                  name='content'
                  // required
                  resize={ResizeType.VERTICAL}
                  toolbarButtons={[
                    ToolbarOptions.BOLD,
                    ToolbarOptions.ITALIC,
                    ToolbarOptions.UNDERLINE,
                    ToolbarOptions.FORMAT_UL,
                    ToolbarOptions.FORMAT_OL,
                    ToolbarOptions.INSERT_LINK,
                  ]}
                  dataQa={config.pendo.athena.mentorshipProgram.guides.addGuideFlyout.guideContentInputField}
                />
              </div>
              <VideoSection setIsVideoUploading={setIsVideoUploading} />
              <RelatedMaterialsSection setIsRelatedMaterialsUploading={setIsRelatedMaterialsUploading} />
              <DeliverySection />
            </div>
            <div className='d-flex justify-content-end position-sticky p-5 border-top border-gray-6'>
              <Button
                variant='secondary'
                size='lg'
                onClick={handleClose}
                data-qa={config.pendo.athena.mentorshipProgram.guides.addGuideFlyout.flyoutClose}
              >
                {t.FORM.CANCEL()}
              </Button>
              <Button
                variant='primary'
                type='submit'
                size='lg'
                className='ml-3'
                disabled={!formState.isValid || formState.isSubmitting || isVideoUploading || isRelatedMaterialsUploading}
                data-qa={config.pendo.athena.mentorshipProgram.guides.addGuideFlyout.flyoutSubmit}
              >
                {formState.isSubmitting
                  ? (isEditMode ? t.FORM.SAVING() : t.FORM.ADDING())
                  : (isEditMode ? t.FORM.SAVE() : t.FORM.ADD())
                }
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </NvFlyoutModal>
  );
};

export default CreateGuideFlyout;
